import { useEffect, useMemo, useState } from "react";
import api, { fmtTRY } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CalendarDays, ChevronLeft, ChevronRight, BellRing, Clock, AlertTriangle } from "lucide-react";

const TR_MONTHS = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const TR_WEEK = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

export default function NotesCalendarPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1..12
  const [cal, setCal] = useState({ month: "", days: [], stats: { pending_count: 0, pending_total: 0, overdue_count: 0, overdue_total: 0 } });
  const [upcoming, setUpcoming] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [busyId, setBusyId] = useState("");

  const monthStr = `${year}-${String(month).padStart(2, "0")}`;

  const load = async () => {
    const [c, u] = await Promise.all([
      api.get(`/notes/calendar?month=${monthStr}`),
      api.get(`/notes/upcoming?days=15`),
    ]);
    setCal(c.data);
    setUpcoming(u.data);
    setSelectedDay(null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr]);

  const grid = useMemo(() => {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    const startOffset = (first.getDay() + 6) % 7; // Monday-first
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= last.getDate(); d++) {
      const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const bucket = cal.days.find((x) => x.date === iso);
      cells.push({ day: d, iso, total: bucket?.total || 0, items: bucket?.items || [] });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cal, year, month]);

  const prev = () => { if (month === 1) { setMonth(12); setYear(year - 1); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setMonth(1); setYear(year + 1); } else setMonth(month + 1); };
  const today = () => { const d = new Date(); setYear(d.getFullYear()); setMonth(d.getMonth() + 1); };

  const sendReminder = async (n) => {
    setBusyId(n.id);
    try {
      await api.post(`/notes/${n.id}/reminder`);
      toast.success(`Hatırlatma gönderildi: ${n.payer || "müşteri"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Hatırlatma gönderilemedi");
    } finally {
      setBusyId("");
    }
  };

  const sendAllOverdue = async () => {
    const overdue = upcoming.filter((n) => n.overdue);
    if (!overdue.length) return toast.info("Vadesi geçmiş senet/çek yok");
    if (!window.confirm(`${overdue.length} vadesi geçmiş kayda hatırlatma gönderilsin mi?`)) return;
    try {
      await Promise.all(overdue.map((n) => api.post(`/notes/${n.id}/reminder`)));
      toast.success(`${overdue.length} hatırlatma gönderildi`);
      load();
    } catch {
      toast.error("Bazı hatırlatmalar başarısız oldu");
    }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const dayDetails = selectedDay && grid.find((c) => c?.iso === selectedDay);

  return (
    <div className="space-y-5" data-testid="notes-calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Takip</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
            <CalendarDays size={28} className="text-[#D97706]" /> Taksit / Senet Takvimi
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={today} data-testid="cal-today">Bugün</Button>
          <Button
            className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2"
            onClick={sendAllOverdue}
            disabled={cal.stats.overdue_count === 0}
            data-testid="send-all-overdue"
          >
            <BellRing size={16} /> Vadesi Geçene Hatırlatma ({cal.stats.overdue_count})
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Bu Ay · Toplam Bekleyen</div>
          <div className="text-2xl font-extrabold text-[#1B4D3E] mt-1">{fmtTRY(cal.stats.pending_total)}</div>
          <div className="text-xs text-slate-500 mt-0.5">{cal.stats.pending_count} kayıt</div>
        </CardContent></Card>
        <Card className="border-rose-200 bg-rose-50/40"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-rose-700 font-bold flex items-center gap-1"><AlertTriangle size={12} /> Vadesi Geçmiş</div>
          <div className="text-2xl font-extrabold text-rose-700 mt-1">{fmtTRY(cal.stats.overdue_total)}</div>
          <div className="text-xs text-rose-600/80 mt-0.5">{cal.stats.overdue_count} kayıt</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1"><Clock size={12} /> 15 Günde Vadesi</div>
          <div className="text-2xl font-extrabold text-[#D97706] mt-1">{upcoming.filter((n) => !n.overdue).length}</div>
          <div className="text-xs text-slate-500 mt-0.5">yaklaşan senet/çek</div>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="p-4">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Yoğun Gün</div>
          {(() => {
            const b = (cal.days || []).slice().sort((a, b) => b.total - a.total)[0];
            return b ? (<>
              <div className="text-2xl font-extrabold text-slate-900 mt-1">{fmtTRY(b.total)}</div>
              <div className="text-xs text-slate-500 mt-0.5">{b.date}</div>
            </>) : <div className="text-sm text-slate-400 mt-2">Bu ay kayıt yok</div>;
          })()}
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Calendar */}
        <Card className="border-slate-200 lg:col-span-2">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <Button size="sm" variant="outline" onClick={prev} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
              <div className="text-lg font-extrabold text-slate-900">{TR_MONTHS[month - 1]} {year}</div>
              <Button size="sm" variant="outline" onClick={next} data-testid="cal-next"><ChevronRight size={16} /></Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center">
              {TR_WEEK.map((w) => <div key={w} className="text-[10px] uppercase tracking-widest font-bold text-slate-400 py-1">{w}</div>)}
              {grid.map((c, i) => {
                if (!c) return <div key={i} className="aspect-square" />;
                const isPast = c.iso < todayIso;
                const hasItems = c.items.length > 0;
                const isToday = c.iso === todayIso;
                const isSelected = selectedDay === c.iso;
                return (
                  <button
                    key={i}
                    onClick={() => setSelectedDay(c.iso)}
                    data-testid={`cal-day-${c.iso}`}
                    className={`aspect-square p-1 border rounded-lg flex flex-col items-stretch text-left transition-all ${isSelected ? "border-[#1B4D3E] bg-emerald-50 shadow-sm" : "border-slate-200 hover:bg-slate-50"} ${isToday ? "ring-2 ring-[#D97706]" : ""}`}
                  >
                    <div className={`text-xs font-bold ${isPast && hasItems ? "text-rose-700" : "text-slate-700"}`}>{c.day}</div>
                    {hasItems && (
                      <>
                        <div className={`mt-auto text-[9px] font-mono tabular-nums text-right ${isPast ? "text-rose-700 font-bold" : "text-[#1B4D3E] font-semibold"}`}>{fmtTRY(c.total)}</div>
                        <div className="flex gap-0.5 mt-0.5">
                          {c.items.slice(0, 4).map((it, idx) => <div key={idx} className={`h-1 flex-1 rounded ${isPast ? "bg-rose-500" : it.note_type === "cek" ? "bg-sky-500" : "bg-[#D97706]"}`} />)}
                        </div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-[#D97706]" /> Senet</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-sky-500" /> Çek</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-rose-500" /> Geçmiş</span>
              <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded ring-2 ring-[#D97706]" /> Bugün</span>
            </div>
          </CardContent>
        </Card>

        {/* Selected day OR upcoming */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-widest font-bold text-[#1B4D3E] mb-2">
              {selectedDay ? `${selectedDay} vadeli` : "Yaklaşan 15 Gün"}
            </div>
            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
              {(selectedDay ? (dayDetails?.items || []) : upcoming).map((n) => (
                <div key={n.id} className={`p-3 rounded-lg border ${n.overdue ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-slate-50/40"}`} data-testid={`note-row-${n.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{n.payer || n.customer_name || "—"}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{n.due_date} · {(n.note_type || "senet").toUpperCase()}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-extrabold text-[#1B4D3E] whitespace-nowrap">{fmtTRY(n.amount)}</div>
                      {n.overdue && <Badge className="bg-rose-600 text-white text-[9px] mt-0.5">{n.days_to_due && n.days_to_due < 0 ? `${-n.days_to_due} gün geçti` : "GECİKTİ"}</Badge>}
                      {!n.overdue && n.days_to_due !== undefined && <Badge className="bg-amber-500 text-slate-900 text-[9px] mt-0.5">{n.days_to_due} gün</Badge>}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {n.last_reminder_at && <div className="text-[10px] text-slate-500">Son hatırlatma: {new Date(n.last_reminder_at).toLocaleDateString("tr-TR")}</div>}
                    <Button
                      size="sm"
                      variant="outline"
                      className="ml-auto gap-1 text-xs"
                      onClick={() => sendReminder(n)}
                      disabled={busyId === n.id}
                      data-testid={`send-reminder-${n.id}`}
                    >
                      <BellRing size={12} /> {busyId === n.id ? "Gönderiliyor…" : "Hatırlat"}
                    </Button>
                  </div>
                </div>
              ))}
              {(selectedDay ? (dayDetails?.items || []).length === 0 : upcoming.length === 0) && (
                <div className="text-center text-sm text-slate-400 py-8">
                  {selectedDay ? "Bu gün için kayıt yok" : "Önümüzdeki 15 günde kayıt yok 🎉"}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
